import React, { useEffect, useMemo, useState } from "react";
import { Trash2, CheckCircle2, Plus, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { createGap, deleteGap, listSalonServices } from "../../lib/api";
import { melDate, melLocalInput } from "../../lib/datetime";

/* FYLLR — Salon Quick Add
   ─────────────────────────
   3 stacked day cards (Today / Tomorrow / This week)
   Each contains 3 time-band tabs (Morning / Afternoon / Evening)
   Inside each band:
     - Set service + category + price ONCE at the top
     - Tap any pre-defined time chip → publishes that slot instantly (one tap, optimistic UI)
     - Published slots move into the "live" row with a delete control
   Target: <60 seconds to add a cancelled slot. */

const CATEGORIES = ["Hair", "Skin", "Brows + Lashes", "Body", "Nails", "Sauna", "Contrast Therapy", "Bathhouse", "Float Tank", "Infrared Sauna", "Massage"];

const DAY_TABS = [
  { key: "today", label: "Today" },
  { key: "tomorrow", label: "Tomorrow" },
  { key: "week", label: "This week" },
];

const BAND_DEFS = {
  morning: {
    label: "Morning",
    sub: "6am – 12pm",
    chips: ["6:00", "6:30", "7:00", "7:30", "8:00", "8:30", "9:00", "9:30", "10:00", "10:30", "11:00", "11:30"],
    suffix: "am",
  },
  afternoon: {
    label: "Afternoon",
    sub: "12pm – 5pm",
    chips: ["12:00", "12:30", "1:00", "1:30", "2:00", "2:30", "3:00", "3:30", "4:00", "4:30"],
    suffix: "pm",
  },
  evening: {
    label: "Evening",
    sub: "5pm – 9pm",
    chips: ["5:00", "5:30", "6:00", "6:30", "7:00", "7:30", "8:00", "8:30"],
    suffix: "pm",
  },
};

function dayLabel(dayKey, weekDayIndex) {
  if (dayKey === "today") return "Today";
  if (dayKey === "tomorrow") return "Tomorrow";
  // This week: weekDayIndex offsets from tomorrow + 1
  const offset = 2 + weekDayIndex;
  const dt = new Date();
  dt.setDate(dt.getDate() + offset);
  return dt.toLocaleDateString("en-AU", { weekday: "long" });
}

/* Build the time string we save to the API.
   Example: "Today · 3:30pm" or "Friday · 9:00am" */
function buildTimeString(dayKey, weekDayIndex, chip, suffix) {
  const day = dayLabel(dayKey, weekDayIndex);
  return `${day} · ${chip}${suffix}`;
}

/* Absolute local start ("YYYY-MM-DDTHH:MM") for a chip, so the feed shows a real date. */
function chipStart(dayKey, weekDayIndex, chip, suffix) {
  const offset = dayKey === "today" ? 0 : dayKey === "tomorrow" ? 1 : 2 + weekDayIndex;
  let [h, m] = chip.split(":").map(Number);
  if (suffix === "pm" && h !== 12) h += 12;
  if (suffix === "am" && h === 12) h = 0;
  return `${melDate(offset)}T${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function matchesBand(timeString, dayKey, weekDayIndex, bandKey) {
  if (!timeString) return false;
  const expectedDay = dayLabel(dayKey, weekDayIndex);
  if (!timeString.toLowerCase().startsWith(expectedDay.toLowerCase())) return false;
  const t = timeString.toLowerCase();
  if (bandKey === "morning") return t.includes("am") || t.includes("12:00pm") === false && t.includes(":") && t.includes("am");
  if (bandKey === "afternoon") return /(12:|1:|2:|3:|4:)/.test(t) && t.includes("pm");
  if (bandKey === "evening") return /(5:|6:|7:|8:|9:)/.test(t) && t.includes("pm");
  return false;
}

export default function QuickAddGaps({ salonSlug, suburb, gaps, onChange }) {
  const [activeDay, setActiveDay] = useState("today");
  const [activeBand, setActiveBand] = useState("morning");
  const [weekDayIdx, setWeekDayIdx] = useState(0);
  const [service, setService] = useState("");
  const [category, setCategory] = useState("Hair");
  const [originalPrice, setOriginalPrice] = useState("");
  const [offerPrice, setOfferPrice] = useState("");
  const [pendingChip, setPendingChip] = useState(null);
  const [duration, setDuration] = useState(60);
  // Phase 2A: standing-menu shortcut. Populated once on mount.
  const [menuServices, setMenuServices] = useState([]);
  const [pickedServiceId, setPickedServiceId] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await listSalonServices();
        if (!cancelled) setMenuServices(data || []);
      } catch (_e) { /* non-fatal — manual entry still works */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const applyMenuService = (id) => {
    setPickedServiceId(id);
    const svc = menuServices.find((s) => s.id === id);
    if (!svc) return;
    setService(svc.name);
    setCategory(svc.category);
    setOriginalPrice(String(svc.standard_price));
    setDuration(svc.duration_min);
    // Suggest 30% off as a starting point — salon can tweak before tapping a chip.
    setOfferPrice(String(Math.max(1, Math.round(svc.standard_price * 0.7))));
    toast.success(`Loaded "${svc.name}" from menu.`);
  };

  const band = BAND_DEFS[activeBand];

  // Map chip → existing gap id (matched on real start time; falls back to legacy labels)
  const chipToGapId = useMemo(() => {
    const map = {};
    const band = BAND_DEFS[activeBand];
    (gaps || []).forEach((g) => {
      if (g.appointment_at) {
        const key = melLocalInput(new Date(g.appointment_at));
        band.chips.forEach((chip) => {
          if (!map[chip] && chipStart(activeDay, weekDayIdx, chip, band.suffix) === key) map[chip] = g.id;
        });
      } else if (matchesBand(g.time, activeDay, weekDayIdx, activeBand)) {
        const m = g.time.match(/(\d{1,2}:\d{2})/);
        if (m && !map[m[1]]) map[m[1]] = g.id;
      }
    });
    return map;
  }, [gaps, activeDay, weekDayIdx, activeBand]);

  const liveChips = useMemo(() => new Set(Object.keys(chipToGapId)), [chipToGapId]);

  const formReady = service.trim() && originalPrice && offerPrice;

  const publishChip = async (chip) => {
    if (!formReady) {
      toast.error("Set service, usual $, and offer $ first.");
      return;
    }
    if (Number(offerPrice) >= Number(originalPrice)) {
      toast.error("Offer must be lower than usual.");
      return;
    }
    if (Number(offerPrice) < 5) {
      toast.error("Minimum offer price is $5.");
      return;
    }
    if (liveChips.has(chip)) {
      toast.message("Already live for this time.");
      return;
    }
    setPendingChip(chip);
    try {
      await createGap({
        salon_slug: salonSlug,
        service: service.trim(),
        category,
        suburb: suburb || "Fitzroy",
        time: buildTimeString(activeDay, weekDayIdx, chip, band.suffix),
        starts_at_local: chipStart(activeDay, weekDayIdx, chip, band.suffix),
        duration_min: Number(duration) || 60,
        original_price: Number(originalPrice),
        offer_price: Number(offerPrice),
      });
      toast.success(`Live · ${chip}${band.suffix}`);
      onChange && onChange();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not publish.";
      toast.error(typeof msg === "string" ? msg : "Failed.");
    } finally {
      setPendingChip(null);
    }
  };

  const removeChip = async (chip) => {
    const id = chipToGapId[chip];
    if (!id) return;
    try {
      await deleteGap(id);
      toast.success("Removed.");
      onChange && onChange();
    } catch {
      toast.error("Could not remove.");
    }
  };

  return (
    <section className="bg-bone border border-sand/70 rounded-2xl overflow-hidden" data-testid="quick-add-card">
      {/* Day selector */}
      <div className="flex border-b border-sand/70">
        {DAY_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => { setActiveDay(t.key); if (t.key !== "week") setWeekDayIdx(0); }}
            className={`flex-1 min-h-11 py-3 text-[11px] tracking-[0.22em] uppercase transition-colors ${
              activeDay === t.key ? "bg-espresso text-bone" : "text-ink/60 hover:text-ink"
            }`}
            data-testid={`quick-day-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Week-day picker — only when "This week" tab is active */}
      {activeDay === "week" && (
        <div className="px-5 py-3 border-b border-sand/70 flex gap-2 overflow-x-auto" data-testid="week-day-picker">
          {Array.from({ length: 5 }).map((_, i) => {
            const dt = new Date();
            dt.setDate(dt.getDate() + 2 + i);
            const label = dt.toLocaleDateString("en-AU", { weekday: "short", day: "numeric" });
            const active = weekDayIdx === i;
            return (
              <button
                key={i}
                onClick={() => setWeekDayIdx(i)}
                className={`shrink-0 min-h-11 px-3.5 py-1.5 rounded-full text-xs border transition-colors ${
                  active ? "bg-espresso text-bone border-espresso" : "border-mocha/30 text-mocha hover:border-espresso hover:bg-bone"
                }`}
                data-testid={`week-day-${i}`}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      {/* Band tabs */}
      <div className="flex border-b border-sand/70">
        {Object.entries(BAND_DEFS).map(([k, b]) => (
          <button
            key={k}
            onClick={() => setActiveBand(k)}
            className={`flex-1 min-h-11 py-2 px-3 text-[11px] tracking-[0.18em] uppercase transition-colors ${
              activeBand === k ? "text-espresso border-b-2 border-mocha" : "text-mocha/70 hover:text-espresso"
            }`}
            data-testid={`quick-band-${k}`}
          >
            {b.label}
            <span className="block text-[9px] tracking-[0.12em] text-mocha/55 mt-0.5">{b.sub}</span>
          </button>
        ))}
      </div>

      {/* Phase 2A: Load from standing menu (two-tap flow) */}
      {menuServices.length > 0 && (
        <div className="px-5 py-3 border-b border-sand/70 bg-warm/10 flex items-center gap-3" data-testid="menu-shortcut">
          <BookOpen size={14} strokeWidth={1.5} className="text-mocha shrink-0" />
          <label className="flex-1 min-w-0">
            <span className="block text-[10px] tracking-[0.22em] uppercase text-mocha mb-1">Load from menu</span>
            <select
              value={pickedServiceId}
              onChange={(e) => applyMenuService(e.target.value)}
              className="w-full min-h-11 border border-sand rounded-md px-3 py-2 outline-none focus:border-espresso text-[14px] bg-bone"
              data-testid="menu-service-select"
            >
              <option value="">Pick a saved service…</option>
              {menuServices.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} — {s.category} · {s.duration_min}min · ${s.standard_price}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {/* Defaults row */}
      <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-12 gap-3 border-b border-sand/70 bg-bone">
        <label className="sm:col-span-5">
          <span className="block text-[10px] tracking-[0.22em] uppercase text-mocha mb-1.5">Service</span>
          <input
            type="text"
            value={service}
            onChange={(e) => setService(e.target.value)}
            placeholder="e.g. Cut & colour"
            className="w-full min-h-11 border border-sand rounded-md px-3 py-2 outline-none focus:border-espresso text-[14px] bg-bone"
            data-testid="quick-service"
          />
        </label>
        <label className="sm:col-span-3">
          <span className="block text-[10px] tracking-[0.22em] uppercase text-mocha mb-1.5">Category</span>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full min-h-11 border border-sand rounded-md px-3 py-2 outline-none focus:border-espresso text-[14px] bg-bone"
            data-testid="quick-category"
          >
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="sm:col-span-2">
          <span className="block text-[10px] tracking-[0.22em] uppercase text-mocha mb-1.5">Usual $</span>
          <input
            type="number"
            value={originalPrice}
            onChange={(e) => setOriginalPrice(e.target.value)}
            placeholder="380"
            className="w-full min-h-11 border border-sand rounded-md px-3 py-2 outline-none focus:border-espresso text-[14px] bg-bone"
            data-testid="quick-original"
          />
        </label>
        <label className="sm:col-span-2">
          <span className="block text-[10px] tracking-[0.22em] uppercase text-mocha mb-1.5">Offer $</span>
          <input
            type="number"
            value={offerPrice}
            onChange={(e) => setOfferPrice(e.target.value)}
            placeholder="245"
            min="5"
            className="w-full min-h-11 border border-sand rounded-md px-3 py-2 outline-none focus:border-espresso text-[14px] bg-bone"
            data-testid="quick-offer"
          />
          <span className="block text-[10px] text-mocha/60 mt-1">$5 minimum</span>
        </label>
      </div>

      {/* Time chips */}
      <div className="px-5 py-5">
        <p className="text-[10px] tracking-[0.22em] uppercase text-mocha mb-3">
          Choose day, time band, then a time · publishes instantly
        </p>
        <div className="flex flex-wrap gap-2">
          {band.chips.map((chip) => {
            const live = liveChips.has(chip);
            const pending = pendingChip === chip;
            const past = !live && chipStart(activeDay, weekDayIdx, chip, band.suffix) <= melLocalInput();
            return (
              <button
                key={chip}
                onClick={() => (live ? removeChip(chip) : publishChip(chip))}
                disabled={pending || past}
                className={`group relative inline-flex min-h-11 items-center gap-1.5 px-3.5 py-2 rounded-full text-[13px] border transition-all ${
                  live
                    ? "bg-espresso text-bone border-espresso hover:bg-ink"
                    : pending
                    ? "bg-espresso text-bone border-espresso"
                    : past
                    ? "border-sand/50 text-ink/30 cursor-not-allowed"
                    : "border-sand text-ink/80 hover:border-espresso hover:text-espresso"
                }`}
                data-testid={`quick-chip-${chip}`}
              >
                {live ? <CheckCircle2 size={12} strokeWidth={1.75} /> : pending ? null : <Plus size={11} strokeWidth={2} className="opacity-50" />}
                <span>{chip}{band.suffix}</span>
                {live && <Trash2 size={11} strokeWidth={1.5} className="opacity-0 group-hover:opacity-100 transition-opacity ml-0.5" />}
              </button>
            );
          })}
        </div>
        {!formReady && (
          <p className="mt-4 text-xs text-ink/50">
            Set service + prices above, then tap times to publish.
          </p>
        )}
      </div>
    </section>
  );
}
